import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CustomButton,
  CustomTextInput,
  CustomModal,
  CustomLoader,
} from "../../../components";
import AxiosInstance, { TokenService } from "../../../services/Api";
import styles from "../plans-pricing/PlansPricing.module.css";

const LIST_PATH = "/dashboard/masters/plan-features";

const isSuccess = (response) => response?.data?.status === true;

const getUserId = () => {
  const user = TokenService.getUser();
  return user?.id || user?.user_id || 1;
};

const CreatePlanFeature = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editingFeatureId = location.state?.feature?.id;
  const isEditMode = Boolean(editingFeatureId);

  const [formData, setFormData] = useState({
    feature_name: location.state?.feature?.feature_name || "",
    feature_description: location.state?.feature?.feature_description || "",
  });
  const [errors, setErrors] = useState({});
  const [fetchLoading, setFetchLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const fetchFeature = useCallback(async () => {
    if (!editingFeatureId) return;
    setFetchLoading(true);
    try {
      const response = await AxiosInstance.post("/saas/view/plan_feature", {
        feature_id: editingFeatureId,
      });

      if (isSuccess(response) && response?.data?.data) {
        const item = response.data.data;
        setFormData({
          feature_name: item.feature_name || "",
          feature_description: item.feature_description || "",
        });
      } else {
        throw new Error(response?.data?.message || "Failed to fetch feature details");
      }
    } catch (err) {
      setStatusModal({
        open: true,
        type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Error fetching feature details",
      });
    } finally {
      setFetchLoading(false);
    }
  }, [editingFeatureId]);

  useEffect(() => {
    fetchFeature();
  }, [fetchFeature]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.feature_name.trim())
      newErrors.feature_name = "Feature name is required";
    if (!formData.feature_description.trim())
      newErrors.feature_description = "Feature description is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = isEditMode
        ? {
            feature_id: editingFeatureId,
            feature_name: formData.feature_name.trim(),
            feature_description: formData.feature_description.trim(),
            updated_by: getUserId(),
          }
        : {
            feature_name: formData.feature_name.trim(),
            feature_description: formData.feature_description.trim(),
            created_by: getUserId(),
          };

      const response = isEditMode
        ? await AxiosInstance.post("/saas/update/plan_feature", payload)
        : await AxiosInstance.post("/saas/create/plan_feature", payload);

      if (isSuccess(response)) {
        setStatusModal({
          open: true,
          type: "success",
          message:
            response?.data?.message ||
            (isEditMode
              ? "Feature updated successfully"
              : "Feature created successfully"),
        });
      } else {
        throw new Error(
          response?.data?.message ||
            `Failed to ${isEditMode ? "update" : "create"} feature`,
        );
      }
    } catch (err) {
      setStatusModal({
        open: true,
        type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          `Error ${isEditMode ? "updating" : "creating"} feature`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusOk = () => {
    const wasSuccess = statusModal.type === "success";
    setStatusModal((prev) => ({ ...prev, open: false }));
    if (wasSuccess) navigate(LIST_PATH);
  };

  if (fetchLoading) {
    return (
      <div>
        <div className={styles.loadingContainer}>
          <CustomLoader text="Loading feature details..." />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>
            {isEditMode ? "Edit Feature" : "Create Features"}
          </h1>
          <p className={styles.pageSubtitle}>
            Tailor your iFillip subscription with customizable feature
            modules.
          </p>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.formGrid}>
          <div className={styles.fullRow}>
            <CustomTextInput
              label="Feature Name"
              name="feature_name"
              value={formData.feature_name}
              onChange={handleChange}
              placeholder="e.g. Dashboard"
              required
              error={errors.feature_name}
            />
          </div>

          <div className={styles.fullRow}>
            <CustomTextInput
              label="Feature Description"
              name="feature_description"
              value={formData.feature_description}
              onChange={handleChange}
              placeholder="Describe what this feature offers"
              multiline
              rows={4}
              required
              error={errors.feature_description}
            />
          </div>
        </div>
      </div>

      <div className={styles.formActions}>
        <CustomButton
          variant="outline-dark"
          fullWidth={false}
          onClick={() => navigate(LIST_PATH)}
          disabled={submitting}
        >
          Cancel
        </CustomButton>
        <CustomButton
          variant="primary"
          fullWidth={false}
          className={styles.btnOrange}
          onClick={handleSubmit}
          loading={submitting}
          loadingText={isEditMode ? "Updating..." : "Creating..."}
        >
          {isEditMode ? "Update Feature" : "Create Features"}
        </CustomButton>
      </div>

      <CustomModal
        isOpen={statusModal.open}
        onClose={handleStatusOk}
        title={statusModal.type === "success" ? "Success" : "Failed"}
        size="sm"
      >
        <p className={styles.confirmText}>{statusModal.message}</p>
        <div className={styles.confirmActions}>
          <CustomButton
            variant="primary"
            fullWidth={false}
            className={styles.btnOrange}
            onClick={handleStatusOk}
          >
            OK
          </CustomButton>
        </div>
      </CustomModal>
    </div>
  );
};

export default CreatePlanFeature;
